import { Button, Checkbox, Divider, Tooltip } from "antd";
import Search from "antd/lib/input/Search";
import {
  DropDownDBFeatureInstance,
  DropdownEntityOption,
  TEditingEntity,
  TEntityLink,
  TLinkSource
} from "components/feature-entity-linking-page/models";
import {
  getTLinkSourceMaterialObject,
  translateFeatureToOptions
} from "components/feature-entity-linking-page/utils";
import { useEffect, useMemo, useState } from "react";
import BlueTickIcon from "components/svg-icons/blue-tick-icon";
import { InfoCircleOutlined } from "@ant-design/icons";
import PredecessorSuccessorTag from "../cards/material-predecessor-successor-tag";
import PredecessorMaterialInstruction from "../instructions/predessesor-material-instruction";

export default function MaterialListing({
  materials,
  editingEntity,
  onUpdateEditingEntityList,
  selectedEntityInfo
}: {
  materials: Array<DropDownDBFeatureInstance>;
  editingEntity: TEditingEntity;
  onUpdateEditingEntityList: (props: { updatedlist: TLinkSource[] }) => void;
  selectedEntityInfo: TEntityLink | undefined;
}) {
  const [specNumber, setSpecNumber] = useState<string | null>();
  const [searchText, setSearchText] = useState<string>("");
  const [isSuggested, setSuggested] = useState(false);
  const [isAllSuggestedSelected, setAllSuggestedSelected] = useState(false);
  const [entities, setEntities] = useState<DropdownEntityOption[]>([]);
  const [selectedIds, setSelectedIds] = useState<
    Array<{ id: string; isSuggested: boolean | undefined }>
  >([]);

  const allEntities = useMemo(() => {
    if (editingEntity) {
      const allMaterial = translateFeatureToOptions(
        materials,
        editingEntity.entityDetail.specSection.section_number
      );

      setEntities(allMaterial);

      return allMaterial;
    }
    return [];
  }, [materials, editingEntity]);

  useEffect(() => {
    setSpecNumber(editingEntity.entityDetail.specSection.section_number);
    const ids = editingEntity.list
      .map((x) =>
        x.feature_instance?.id
          ? {
              id: x.feature_instance.id,
              isSuggested: x.isSuggested
            }
          : undefined
      )
      .filter((x) => x !== undefined);

    const a = ids.filter((x) => x?.isSuggested).length;
    const b = allEntities.filter((x) => x.isSuggested).length;
    setAllSuggestedSelected(a === b);

    setSelectedIds(
      ids as {
        id: string;
        isSuggested: boolean | undefined;
      }[]
    );
  }, [editingEntity, allEntities]);

  useEffect(() => {
    let filterAfterSearch = allEntities.filter((x) =>
      x.label.includes(searchText)
    );
    if (isSuggested) {
      filterAfterSearch = filterAfterSearch.filter((x) => x.isSuggested);
    }
    setEntities(filterAfterSearch);
  }, [allEntities, isSuggested, searchText, specNumber]);

  const onChangeCheckbox = (checked: boolean, item: DropdownEntityOption) => {
    if (checked) {
      const sampleObject = getTLinkSourceMaterialObject(item);
      onUpdateEditingEntityList({
        updatedlist: [...editingEntity.list, sampleObject]
      });
    } else {
      onUpdateEditingEntityList({
        updatedlist: editingEntity.list.filter(
          (x) => x.featureInstanceId !== item.id
        )
      });
    }
  };

  const isMaterialPredecessor = (val: any) => {
    return val?.instance?.successor_material_links?.length > 0;
  };

  const showPredecessorInstructions = () => {
    if (
      selectedEntityInfo &&
      (selectedEntityInfo?.predecessor_material_links?.length ?? 0) > 0
    )
      return true;
    return false;
  };

  const btnCss = useMemo(() => {
    return isSuggested
      ? "text-ciq-blue-600 border-ciq-blue-600 focus:text-ciq-blue-600 focus:border-ciq-blue-600"
      : "text-black text-opacity-90 border-ciq-gray-100 focus:text-black text-opacity-90 focus:border-ciq-gray-100";
  }, [isSuggested]);

  return (
    <div className="flex flex-col h-full w-full features-card">
      <div className="flex items-center justify-between px-4">
        <div
          className="flex font-bold py-2"
          data-testid="materiallisting-title"
        >
          List of Materials
        </div>
        <div
          className="flex space-x-1 ml-auto"
          style={{ color: "#000000", opacity: "45%" }}
          data-testid="materiallisting-info-label"
        >
          <InfoCircleOutlined className="pr-[5px] pt-[4px]" />
          Materials are Linked as Successors
        </div>
        <div className="flex space-x-1">
          {isSuggested && !showPredecessorInstructions && (
            <Checkbox
              checked={isAllSuggestedSelected}
              data-testid="materiallisting-checkbox-all"
              onChange={(e) => {
                // setAllSuggestedSelected(e.target.checked);
                if (e.target.checked) {
                  const suggestedObjects = allEntities
                    .filter((x) => x.isSuggested)
                    .map((item) => getTLinkSourceMaterialObject(item));
                  const nonSuggestedLinkedObjects = editingEntity.list.filter(
                    (x) => !x.isSuggested
                  );
                  onUpdateEditingEntityList({
                    updatedlist: [
                      ...nonSuggestedLinkedObjects,
                      ...suggestedObjects
                    ]
                  });
                } else {
                  const nonSuggestedLinkedObjects = editingEntity.list.filter(
                    (x) => !x.isSuggested
                  );
                  onUpdateEditingEntityList({
                    updatedlist: nonSuggestedLinkedObjects
                  });
                }
              }}
            >
              {isAllSuggestedSelected ? "Unlink All" : "Link All"}
            </Checkbox>
          )}
        </div>
      </div>
      {isSuggested && !showPredecessorInstructions && (
        <div
          className="material-linking--suggestedLblDiv"
          data-testid="materiallisting-suggested-label"
        >
          <span className="material-linking--suggestInfoLbl ml-1">
            Suggested materials have the same spec section as the selected
            submittal.
          </span>
        </div>
      )}
      <div className="flex w-full pb-4 px-4">
        <Search
          placeholder="Search Material Name or ID"
          className={`w-full pr-[10px] ${
            showPredecessorInstructions() ? "opacity-50 " : "opacity-100"
          }`}
          onChange={(e) => {
            const text = (e.target.value || "").toLowerCase().trim();
            setSearchText(text);
          }}
          data-testid="materiallisting-search-material-name-id"
          disabled={showPredecessorInstructions()}
        />

        <Divider type="vertical" className="h-7 m-0 mr-4 pt-[35px]" />
        <Button
          icon={isSuggested ? <BlueTickIcon /> : null}
          className={`${btnCss} flex justify-center items-center  rounded p-1 gap-x-1 px-3`}
          onClick={() => setSuggested(!isSuggested)}
          data-testid="materiallisting-button-filter-suggestions"
        >
          <span>Filter Suggestions</span>
        </Button>
      </div>
      {showPredecessorInstructions() && <PredecessorMaterialInstruction />}
      {!showPredecessorInstructions() && (
        <div className="flex flex-col w-full overflow-y-auto h-full">
          {entities?.map((item) => (
            <div
              key={item.id}
              className="flex justify-between px-4 py-3 border-color border-0 border-b border-ciq-gray-400-alt-4 rounded-sm"
            >
              <Checkbox
                disabled={
                  selectedEntityInfo?.feature_type_id === 2 &&
                  isMaterialPredecessor(item)
                }
                checked={selectedIds.some((x) => x.id === item.id)}
                onChange={(e) => {
                  onChangeCheckbox(e.target.checked, item);
                }}
                data-testid="materiallisting-checkbox-item"
              >
                <Tooltip
                  className="flex items-center space-x-2"
                  data-testid="materiallisting-tooltip-item"
                  title={
                    selectedEntityInfo?.feature_type_id === 2 &&
                    isMaterialPredecessor(item)
                      ? "Cannot be linked as this Material is a Predecessor to other Materials."
                      : ""
                  }
                >
                  <i>{item.pre}</i>
                  <div>{item.title}</div>
                </Tooltip>
              </Checkbox>
              {selectedEntityInfo?.feature_type_id === 2 &&
                isMaterialPredecessor(item) && (
                  <PredecessorSuccessorTag
                    title="Predecessor for another Material"
                    data-testid="materiallisting-predecessor-successor-tag"
                  />
                )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
